import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faDownload, faFloppyDisk, faPencil } from '@fortawesome/pro-solid-svg-icons';
import React from 'react';
import { PAGE_PATHS } from './base_report';
import { Button } from 'components/ui/button';
import Dropdown from 'components/dropdown';
import LoadingSpinner from 'components/ui/loading_spinner';
import PropTypes from 'prop-types';

export const GENERATING_CSV_STEPS = {
  DEFAULT: 'default',
  GENERATING: 'generating',
  GENERATED: 'generated',
};

export default function ReportHeaderActions({
  allSelected,
  csvDownloadPath,
  disablePdf = false,
  downloadKey,
  generatingCSVStep,
  hideColumnCustomization = false,
  hideSavedReports = false,
  ransackParams,
  savedReport,
  selectedColumns,
  selectedRecordIds,
  timeZone,
  onEditColumns,
  onPathChanged,
  onSaveAs,
  onUpdateReport
}) {
  const csvReturnFields = () => selectedColumns.map(column => column.label);
  // With PDF off and columns hidden, Actions would open on nothing.
  const hasActions = !disablePdf || !hideColumnCustomization;

  function createCsvPath() {
    const url = new URL(csvDownloadPath, window.location.origin);
    if (allSelected || selectedRecordIds.length === 0) {
      Object.entries(ransackParams()).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((individualValue) => {
            url.searchParams.append(`${key}[]`, individualValue);
          });
        } else {
          url.searchParams.append(key, value);
        }
      });
    } else {
      selectedRecordIds.forEach(recordId => url.searchParams.append('ids[]', recordId));
    }
    csvReturnFields().forEach(field => url.searchParams.append('columns[]', field));
    if (timeZone && !url.searchParams.has('time_zone')) {
      url.searchParams.append('time_zone', timeZone);
    }
    url.searchParams.append('key', downloadKey.current);
    return url.toString();
  }

  const renderSaveDropdownActions = () => {
    // Only a view's creator can save over it; anyone else saves a copy.
    if (!savedReport?.owned) {
      return (
        <div>
          <Button icon={faFloppyDisk} label="Save As" onClick={onSaveAs} />
        </div>
      );
    } else {
      return (
        <Dropdown icon={faFloppyDisk} id="save-dropdown" label="Save" variant="primary">
          <Dropdown.Item
            key="save-as-report"
            name="save-as-report"
            onClick={onSaveAs}
          >
            Save As
          </Dropdown.Item>
          <Dropdown.Item
            key="save-report"
            onClick={onUpdateReport}
          >
            Save
          </Dropdown.Item>
        </Dropdown>
      );
    }
  };

  return (
    <div className="d-flex gap-3">
      {generatingCSVStep === GENERATING_CSV_STEPS.GENERATING && (
        <div className="text-primary d-flex align-items-center gap-2">
          <LoadingSpinner flex size="md" skipTopBuffer />
          Generating CSV...
        </div>
      )}
      {generatingCSVStep === GENERATING_CSV_STEPS.GENERATED && (
        <div className="text-success d-flex align-items-center gap-2">
          <FontAwesomeIcon icon={faCheck}/>
          Generated CSV
        </div>
      )}
      {csvDownloadPath && (
        <Button
          className="px-2"
          disabled={generatingCSVStep !== GENERATING_CSV_STEPS.DEFAULT}
          href={generatingCSVStep === GENERATING_CSV_STEPS.DEFAULT ? createCsvPath() : ''}
          icon={faDownload}
          variant="ghost"
          onClick={
            () => generatingCSVStep === GENERATING_CSV_STEPS.DEFAULT ? onPathChanged(PAGE_PATHS.GENERATE_CSV) : {}
          }
        />
      )}
      {hasActions && (
        <Dropdown id="actions-dropdown" label="Actions">
          {!disablePdf && (
            <Dropdown.Item
              disabled={selectedRecordIds.length === 0}
              key="generate-pdf"
              onClick={() => onPathChanged(PAGE_PATHS.GENERATE_PDF)}
            >
              <FontAwesomeIcon className="mr-2" fixedWidth icon={faDownload}/>
              Download PDF
            </Dropdown.Item>
          )}
          {!hideColumnCustomization && (
            <Dropdown.Item key="edit-columns" onClick={onEditColumns}>
              <FontAwesomeIcon className="mr-2" fixedWidth icon={faPencil}/>
              Edit Columns
            </Dropdown.Item>
          )}
        </Dropdown>
      )}
      {!hideSavedReports && renderSaveDropdownActions()}
    </div>
  );
}

ReportHeaderActions.propTypes = {
  allSelected: PropTypes.bool.isRequired,
  csvDownloadPath: PropTypes.string,
  disablePdf: PropTypes.bool,
  downloadKey: PropTypes.object.isRequired,
  generatingCSVStep: PropTypes.oneOf(Object.values(GENERATING_CSV_STEPS)).isRequired,
  hideColumnCustomization: PropTypes.bool,
  hideSavedReports: PropTypes.bool,
  ransackParams: PropTypes.func.isRequired,
  savedReport: PropTypes.object,
  selectedColumns: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRecordIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
  timeZone: PropTypes.string,
  onEditColumns: PropTypes.func.isRequired,
  onPathChanged: PropTypes.func.isRequired,
  onSaveAs: PropTypes.func.isRequired,
  onUpdateReport: PropTypes.func.isRequired,
};
